import React, { useState } from 'react';
import IconButton from '@mui/material/IconButton';
import MoreHorizIcon from '@mui/icons-material/MoreHorizOutlined';
import { StyledMenuList } from 'framework/ui/menu';
import TimelineRecordAnchor from 'domain/shared/timeline/layout/BaseTimelineRecordLayout/TimelineRecordAnchor';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Box from '@mui/material/Box';
import { CANCEL_INAPPROPRIATE_FLAGGING_LABEL, INAPPROPRIATE_FLAGGING_LABEL } from '../../constants';
import { dropdownMenuPaperSx } from 'framework/ui/styles';

interface TimelineRecordActionsProps {
  buttons?: React.ReactElement[];
  actions?: React.ReactElement[];
  timelineId: StringOrNumber;
  basePathname?: string;
}

const CLOSE_MODAL_BUTTONS = ['Cancel', '×'];
const NON_TOGGLE_DROPDOWN_BUTTONS = [INAPPROPRIATE_FLAGGING_LABEL, 'Delete', 'Submit', 'Transfer', 'Add to blacklist'];
const MODAL_INTERNAL_CLASSES = ['modal-content', 'modal-body', 'form-check', 'flag-dropdown', 'input-group'];

const TimelineRecordActions = ({ buttons, actions, timelineId, basePathname = '' }: TimelineRecordActionsProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isDropdownHidden, setIsDropdownHidden] = useState(false);
  const id = `timeline-actions-${timelineId}`;

  const isClickInsideModal = (parentOffset?: any) => {
    if (!parentOffset) return false;

    if (MODAL_INTERNAL_CLASSES.some(className => parentOffset?.className?.includes(className))) {
      return true;
    }

    return isClickInsideModal(parentOffset?.offsetParent);
  };

  const toggleDropdown = ({ target: { offsetParent, innerText } }: any) => {
    const text = innerText?.replace(/\n/gi, '');
    const isCancelFlagging = text === CANCEL_INAPPROPRIATE_FLAGGING_LABEL;
    const isCloseModalButton = CLOSE_MODAL_BUTTONS.includes(text);
    const isDropdownModalOpen = NON_TOGGLE_DROPDOWN_BUTTONS.includes(text);
    const isClickOnDropdownModal = isClickInsideModal(offsetParent);
    const isClickOutsideModal = isDropdownHidden && !isClickOnDropdownModal;
    const isCloseModalButtonClick = isClickOnDropdownModal && isCloseModalButton;
    const shouldToggleDropdown = !isClickOnDropdownModal && !isDropdownModalOpen && !isCancelFlagging;

    if (isDropdownModalOpen || isClickOutsideModal || isCloseModalButtonClick) {
      setIsDropdownHidden(!isDropdownHidden);
    }

    if (isCloseModalButtonClick || shouldToggleDropdown) {
      setIsDropdownOpen(!isDropdownOpen);

      if (isDropdownModalOpen) {
        setAnchorEl(null);
      }
    }
  };

  const getButtonDropdown = () => {
    return (
      <Box sx={{ position: 'relative', display: 'inline-flex' }}>
        <IconButton
          id={id}
          aria-controls={isDropdownOpen ? id : undefined}
          aria-haspopup="true"
          aria-expanded={isDropdownOpen ? 'true' : undefined}
          onClick={e => {
            if (isDropdownOpen) return;

            setAnchorEl(e.currentTarget);
            toggleDropdown(e);
          }}
          sx={{ padding: '4px', '& .MuiSvgIcon-root': { width: '24px', height: '24px' } }}
        >
          <MoreHorizIcon />
        </IconButton>
        <Popper
          open={isDropdownOpen}
          anchorEl={anchorEl}
          placement="bottom-end"
          transition
          disablePortal
          sx={{ zIndex: 1200 }}
        >
          {({ TransitionProps }) => (
            <Grow {...TransitionProps}>
              <Paper
                elevation={3}
                className={`actions-dropdown-menu ${isDropdownHidden ? 'is-hidden' : ''}`}
                sx={{ ...dropdownMenuPaperSx, ...(isDropdownHidden && { visibility: 'hidden' }) }}
              >
                <ClickAwayListener onClickAway={toggleDropdown}>
                  <StyledMenuList
                    autoFocusItem={false}
                    id={id}
                    aria-labelledby="composition-button"
                    onClick={toggleDropdown}
                  >
                    {buttons}
                  </StyledMenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        position: 'absolute !important',
        right: '18px',
        top: '6px',
        minHeight: '32px',
      }}
    >
      {actions?.length ? actions : null}
      {buttons?.length ? getButtonDropdown() : null}
      {timelineId && <TimelineRecordAnchor timelineId={timelineId} basePathname={basePathname} />}
    </Box>
  );
};

export default TimelineRecordActions;
